'use client';

import { useState, useEffect } from 'react';
import { Cookie } from 'lucide-react';
import {
  COOKIE_SETTINGS_EVENT,
  COOKIE_CONSENT_KEY,
  getConsentExpiryDelay,
  getCookieConsent,
  getCookieConsentExpiry,
  setCookieConsent,
} from '@/lib/cookie-consent';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const sync = () => {
      const consent = getCookieConsent();
      const expiry = getCookieConsentExpiry();
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setVisible(!consent), consent ? getConsentExpiryDelay(expiry!) : 0);
    };
    sync();
    const openSettings = () => setVisible(true);
    const onStorage = (event: StorageEvent) => {
      if (event.key === COOKIE_CONSENT_KEY || event.key === null) sync();
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, openSettings);
    window.addEventListener('storage', onStorage);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(COOKIE_SETTINGS_EVENT, openSettings);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const handleAcceptAll = () => {
    setCookieConsent('accepted');
    setVisible(false);
  };

  const handleNecessaryOnly = () => {
    setCookieConsent('necessary');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="cookie-consent pointer-events-auto fixed bottom-0 left-0 right-0 z-[2147483647] p-4 sm:p-6">
      <div className="mx-auto max-w-4xl bg-white rounded-2xl shadow-2xl border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-12 h-12 rounded-xl bg-[var(--color-brand-light)] items-center justify-center flex-shrink-0">
            <Cookie className="h-6 w-6 text-[var(--color-brand)]" />
          </div>

          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-2">
              Usamos cookies para mejorar tu experiencia
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Futuredge S.L. utiliza cookies propias y de terceros para analizar el uso del sitio.
              Puedes aceptar todas, solo las necesarias, o consultar más información.
              Más info en nuestra{' '}
              <a href="/cookies" className="text-[var(--color-brand)] hover:text-[var(--color-brand-dark)] underline">
                Política de Cookies
              </a>.
            </p>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                onClick={handleNecessaryOnly}
                className="rounded-lg border border-[var(--color-brand)] bg-[var(--color-brand)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--color-brand-dark)]"
              >
                Solo necesarias
              </button>
              <button
                onClick={handleAcceptAll}
                className="rounded-lg border border-[var(--color-brand)] bg-[var(--color-brand)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--color-brand-dark)]"
              >
                Aceptar todas
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
